import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { IconButtonComponent } from '@connect-services/client/components/icon-button/icon-button.component';
import { MainTitleComponent } from '@connect-services/client/components/main-title/main-title.component';
import { ProjectAccessSettingsComponent } from '@connect-services/client/components/project-access-settings/project-access-settings.component';
import { ProjectDepositsSettingsComponent } from '@connect-services/client/components/project-settings/project-deposits-settings/project-deposits-settings.component';
import { ProjectLayersSettingsComponent } from '@connect-services/client/components/project-settings/project-layers-settings/project-layers-settings.component';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ProjectService } from '@connect-services/client/services/project.service';
import { ShapingProjectService } from '@connect-services/client/services/project/shaping/shaping-project.service';
import { EditProjectShapingStateService } from '@connect-services/client/services/project/shaping/shaping-state/edit-project-shaping-state.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { TabComponent, TabWithState } from '@connect-services/src/lib/components/tab/tab.component';
import { FieldInterventionsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { TabContentDirective } from '@shared/client/directives/tab-content.directive';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IconButtonComponent,
        MainTitleComponent,
        ProjectAccessSettingsComponent,
        ProjectDepositsSettingsComponent,
        ProjectLayersSettingsComponent,
        RessourcesModule,
        TabComponent,
        TabContentDirective,
    ],
    selector: 'app-project-settings',
    styleUrl: './project-settings.component.scss',
    templateUrl: './project-settings.component.html',
})
export class ProjectSettingsComponent implements OnInit {
    protected readonly ActionsEnum = ActionsEnum;
    protected readonly canDeleteProject = computed(() => {
        if (!this.projectSettingsPermissionsService.canDeleteProjectBase()) {
            return false;
        }

        if (this.projectSettingsPermissionsService.canManageFieldInterventions()) {
            return true;
        }

        if (!this.interventionsLoaded()) {
            return false;
        }

        return !this.hasInterventions();
    });
    protected readonly defaultTabIndex = signal(1);
    protected readonly hasEditPermissions = computed(() => this.projectSettingsPermissionsService.canEditProject());
    protected readonly projectBloc = inject(ProjectBloc);
    protected readonly projectService = inject(ProjectService);
    protected readonly tabs: TabWithState[] = [];

    private readonly fieldInterventionServiceSdk = inject(FieldInterventionsService);
    private readonly hasInterventions = signal(false);
    private readonly interventionsLoaded = signal(false);
    private readonly loadedProjectId = signal<string | null>(null);
    private readonly matDialog = inject(MatDialog);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly router = inject(Router);
    private readonly shapingProjectService = inject(ShapingProjectService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        const canSeeAccessTab = this.projectSettingsPermissionsService.canSeeAccessTab();
        const canSeeLayersTab = this.projectSettingsPermissionsService.canSeeLayersTab();
        const canSeeDepositsTab = this.projectSettingsPermissionsService.canSeeDepositsTab();

        this.tabs = [
            {
                active: true,
                disabled: !canSeeAccessTab,
                disabledTooltipKey: 'insufficientPermissions',
                name: 'access',
            },
            {
                active: true,
                disabled: !canSeeLayersTab,
                disabledTooltipKey: 'insufficientPermissions',
                name: 'layer',
            },
            {
                active: true,
                disabled: !canSeeDepositsTab,
                disabledTooltipKey: 'insufficientPermissions',
                name: 'deposit',
            },
        ];

        effect(async () => {
            const project = this.projectBloc.value();
            if (!project) {
                this.hasInterventions.set(false);
                this.interventionsLoaded.set(false);
                this.loadedProjectId.set(null);

                return;
            }

            if (this.loadedProjectId() === project.id) {
                return;
            }

            this.hasInterventions.set(false);
            this.interventionsLoaded.set(false);

            await untracked(async () => {
                await this.setCountInterventions();
                this.interventionsLoaded.set(true);
                this.loadedProjectId.set(project.id);
            });
        });
    }

    public ngOnInit(): void {
        const LAYERS_TAB_INDEX = 1;
        const DEPOSITS_TAB_INDEX = 2;
        this.defaultTabIndex.set(
            this.router.url.includes(RoutePathEnum.DEPOSIT_DETAIL) ? DEPOSITS_TAB_INDEX : LAYERS_TAB_INDEX,
        );
    }

    public openDeleteProjectModal(): void {
        if (!this.projectBloc.value() || !this.canDeleteProject()) {
            return;
        }

        this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.hasInterventions()
                    ? this.translocoService.translate('warningInterventionOnProject')
                    : this.translocoService.translate('confirmDeleteProject'),
                onConfirm: () => this.projectService.deleteProject(this.projectBloc.value()!),
                title: this.hasInterventions()
                    ? this.translocoService.translate('confirmDeleteProject')
                    : this.translocoService.translate('warning'),
                type: ModalType.DANGER,
            },
            maxWidth: this.hasInterventions() ? '40vw' : '25vw',
        });
    }

    protected openEditProjectModal(): void {
        this.shapingProjectService.openShapingProjectModal(EditProjectShapingStateService);
    }

    private async setCountInterventions(): Promise<void> {
        const interventions = await firstValueFrom(
            this.fieldInterventionServiceSdk.fieldInterventionControllerGet({
                project: this.projectBloc.value()!.id,
                take: 1,
            }),
        );

        this.hasInterventions.set(!!interventions.items?.length);
    }
}
